import classNames from 'classnames';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { FC, useCallback, useMemo } from 'react';
import ScrollIntoViewIfNeeded from 'react-scroll-into-view-if-needed';

import { htmlDecode } from '../../../utils/html-decode';
import type { SearchResultItemProps } from '../compact-selector/utils';
import { searchedTextInBold } from '../compact-selector/utils';

interface SalesCounterPartSearchResultItemOwnProps {
  isClientLabelWrapped?: boolean;
}

type SalesCounterPartSearchResultItemProps = SalesCounterPartSearchResultItemOwnProps &
  SearchResultItemProps<ISalesCounterpart>;

export const SalesCounterPartSearchResultItem: FC<SalesCounterPartSearchResultItemProps> = ({
  item,
  onSelected,
  isSelected,
  searchedText,
  getError,
  isClientLabelWrapped = false,
}) => {
  const errors = useMemo(() => (getError !== undefined ? getError(item) : []), [item, getError]);
  const hasErrors = errors.length > 0;
  const errorMessage = hasErrors ? errors?.join(', ') : undefined;
  const onClick = useCallback(() => onSelected(item), [onSelected, item]);
  const { id, label, countryResidenceCode, eliotCode } = item;
  const decodedLabel = useMemo(() => htmlDecode(label), [label]);

  return (
    <ScrollIntoViewIfNeeded
      active={isSelected}
      elementType="tr"
      data-e2e="client-search-item"
      className={classNames({ 'table-active': isSelected, 'text-warning': hasErrors })}
      onClick={onClick}
      title={errorMessage}
    >
      <td className={isClientLabelWrapped ? 'text-wrapped' : 'text-truncate'} data-e2e="client-label">
        {searchedTextInBold(decodedLabel, searchedText)}
      </td>
      <td data-e2e="client-country-residence">{countryResidenceCode}</td>
      <td data-e2e="client-id">{searchedTextInBold(id, searchedText)}</td>
      <td data-e2e="client-eliot-code">{eliotCode ? searchedTextInBold(eliotCode, searchedText) : ''}</td>
    </ScrollIntoViewIfNeeded>
  );
};

SalesCounterPartSearchResultItem.displayName = 'SalesCounterPartSearchResultItem';
